import { SocialIcon } from "../utils/social-icon";
import {
  CreateAffiliationApi,
  ItemPostRating,
  ItemTypes,
  ItemWithMetaData,
  Keyword,
  Rating,
  Source,
  UpdateAffiliationApi,
} from "../api/Items";
import RatingBadges from "../shared-components/RatingBadges";
import KeywordBadges from "../shared-components/KeywordBadges";
import { contentMediaToMediaExternalInternal, PostImage } from "./PostImage";
import { atLeastOneItem, classNames, formattedDate } from "../utils";
import {
  BehaviorCheckboxGroup,
  SystemEditMediaModalActions,
} from "../system/SystemEditMediaModal";

import LoadingSpinner from "../shared-components/LoadingSpinner";
import Divider from "../shared-components/LineDivider";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import React from "react";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import {
  useCreateItemPostRating,
  useRemoveRatingAndKeywordsPostsItem,
} from "../api/ItemsApi";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import { LinkIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { justBehaviorRatings } from "../system/helpers";
import PostArticleTranslation from "./PostArticleTranslation";
import { getDisplayPostTypeText } from "../utils/post-utils";
import {
  affiliationItemToAffiliationApi,
  useAffiliationManagement,
} from "../affiliation/useAffiliationManagement";
import { ReportSummaryItemApi } from "../api/Reports";
import {
  imageUrlExternalToUrlApi,
  ITEM_SOURCE_AFFILIATION,
  itemToAffiliation,
} from "../affiliation/helpers";

export interface PostsSummaryProps {
  content: any;
  ratings: Rating[];
  source: Source | undefined;
  keywords: Keyword[] | undefined;
}

export const PostsAuditQA = ({
  post,
  behaviors,
  handleAction,
  isReadOnly,
  report,
}: {
  post: ItemWithMetaData;
  behaviors: string[];
  handleAction: (action: "remove" | "edit", post?: ItemWithMetaData) => void;
  isReadOnly?: boolean;
  report: ReportSummaryItemApi;
}) => {
  const {
    mutateAsync: createItemRating,
    isLoading: isLoadingItemRating,
    error: errorCreateItemRating,
  } = useCreateItemPostRating();

  const { isLoadingAffiliationUpdate, updateAffiliationAsync } =
    useAffiliationManagement();

  const { modal, show, hide } = useConfirmationDialog();

  const {
    data: removeData,
    mutate: removeItem,
    isLoading: loadingRemove,
    error: errorRemove,
  } = useRemoveRatingAndKeywordsPostsItem();
  const [inEdit, setInEdit] = useState<boolean>(false);
  const [ratingSelections, setRatingSelections] = useState<
    PostRatingBehavior[]
  >([]);
  const isAffiliation = post.type.name === ItemTypes.AFFILIATION;

  useEffect(() => {
    if (removeData) handleAction("remove");
  }, [removeData, handleAction]);

  function onCheckboxChange(ratings: PostRatingBehavior[]) {
    setRatingSelections(ratings);
  }

  function handleRemove() {
    show(
      "",
      "Are you sure you want to remove the post?",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          removeItem({
            item_uuids: [post.item.external_id],
          });
          hide();
        }
      }
    );
  }

  function transformRatingsForPost(ratings: PostRatingBehavior[]): Rating[] {
    return ratings.map((rating) => ({
      external_id: "",
      name: rating.name,
      rater_uuid: "",
      score_type: rating.score_type,
    }));
  }

  function createAffiliationRating(
    affiliation: CreateAffiliationApi
  ): PostRatingBehavior | null {
    const existingRating = affiliation.ratings.find(
      (rating: ItemPostRating) => rating.name === ITEM_SOURCE_AFFILIATION
    );

    if (!existingRating?.name) {
      return null;
    }

    return {
      name: existingRating.name,
      score_type: "recommendation",
    };
  }

  function prepareMediaUrls(mediaUrls: string[] = []): string[] {
    return mediaUrls.map((url) => imageUrlExternalToUrlApi(url));
  }

  function updatePostState(updatedRatings: PostRatingBehavior[]) {
    const transformedRatings = transformRatingsForPost(updatedRatings);
    handleAction("edit", {
      ...post,
      rating: transformedRatings,
    });
  }

  async function handleAffiliationUpdate(ratings: PostRatingBehavior[]) {
    try {
      const affiliation = affiliationItemToAffiliationApi(
        report,
        itemToAffiliation(post)
      );

      const updatedContent = { ...post.item.content };
      const processedMediaUrls = prepareMediaUrls(post.item.content.media_urls);

      const finalRatings = [...ratings];
      const affiliationRating = createAffiliationRating(affiliation);
      if (affiliationRating) {
        finalRatings.push(affiliationRating);
      }

      const updateInput: UpdateAffiliationApi = {
        item_uuid: post.item.external_id,
        content: {
          ...updatedContent,
          media_urls: processedMediaUrls,
          created_at: updatedContent.created_at ?? null,
        },
        ratings: finalRatings,
        keywords: post.keywords,
      };

      await updateAffiliationAsync(updateInput);

      post.item.content.media_urls = [];
      updatePostState(finalRatings);
    } catch (error) {
      console.error("Failed to update affiliation:", error);
    }
  }

  async function handleRegularItemUpdate(ratings: PostRatingBehavior[]) {
    try {
      await createItemRating({
        item_uuid: post.item.external_id,
        ratings,
      });

      updatePostState(ratings);
    } catch (error) {
      console.error("Failed to update item rating:", error);
    }
  }

  function handleUpdateRating(
    action: SystemEditMediaModalActions,
    ratings: PostRatingBehavior[]
  ) {
    if (action !== "save") {
      return;
    }

    if (isAffiliation) {
      handleAffiliationUpdate(ratings);
    } else {
      handleRegularItemUpdate(ratings);
    }
  }

  function handleOnEdit() {
    setInEdit(true);
  }
  function handleSave() {
    handleUpdateRating("save", ratingSelections);
    setInEdit(false);
  }

  function getErrorList() {
    const errors = [];

    if (errorRemove) errors.push("Error Removing ");

    if (errorCreateItemRating) errors.push("Error Creating Ratings");

    return errors;
  }

  const errors = getErrorList();
  return (
    <div
      className={classNames(
        "flex flex-col p-4 rounded-lg shadow-md bg-white overflow-hidden border border-gray-200",
        isLoadingItemRating || isLoadingAffiliationUpdate
          ? "opacity-30 pointer-events-none"
          : "",
        "self-start"
      )}
    >
      <Header
        className="flex flex-nowrap items-center mb-6 cursor-pointer"
        post={post}
      />
      {isAffiliation && (
        <div className="flex gap-4 pb-4 text-xs">
          <div className="flex items-center gap-1 font-medium  border border-gray-400 rounded-md px-2 py-1">
            <span>Source:</span>
            <span className="font-light text-gray-500 ">
              {post.item.content.affiliation_source}
            </span>
          </div>

          <div className="flex items-center gap-1 font-medium border border-gray-400 rounded-md px-2 py-1">
            <span>Activity:</span>
            <span className="font-light text-gray-500">
              {post.item.content.affiliation_activity}
            </span>
          </div>
        </div>
      )}
      {!inEdit && <BehaviorFlags className="mt-2" post={post} />}
      {post.keywords && atLeastOneItem(post.keywords) && (
        <KeywordBadges keywords={post.keywords} />
      )}

      {inEdit && (
        <InlineBehaviorCheck
          behaviors={behaviors}
          ratings={post.rating}
          onChange={onCheckboxChange}
        />
      )}
      <Body className="flex flex-col gap-4 mt-4" post={post} />
      <PostArticleTranslation item={post} isSummary={false} />
      <Divider addMargin={false} className={"mt-3"} />
      <div className="flex justify-between gap-4 mt-1">
        <Buttons
          isReadOnly={!!isReadOnly}
          inEdit={inEdit}
          handleOnEdit={handleOnEdit}
          handleRemove={handleRemove}
          handleSave={handleSave}
        />
      </div>
      {errors.length > 0 && <ErrorList list={errors} />}
      {(loadingRemove || isLoadingItemRating) && <LoadingSpinner />}
      {modal}
    </div>
  );
};

type InlineBehaviorCheckProps = {
  behaviors: string[];
  ratings: Rating[];
  onChange: (ratings: PostRatingBehavior[]) => void;
};

function InlineBehaviorCheck({
  behaviors,
  ratings,
  onChange,
}: InlineBehaviorCheckProps) {
  return (
    <>
      <div className="grid grid-cols-2 mt-2 flex-wrap gap-3 border rounded-md p-2">
        <BehaviorCheckboxGroup
          behaviors={behaviors}
          initialSelection={ratings ?? []}
          onChange={onChange}
        />
      </div>
    </>
  );
}

const Buttons = ({
  isReadOnly,
  inEdit,
  handleOnEdit,
  handleSave,
  handleRemove,
}: {
  isReadOnly: boolean;
  inEdit: boolean;
  handleOnEdit: () => void;
  handleSave: () => void;
  handleRemove: () => void;
}) => {
  return (
    <>
      {!isReadOnly && !inEdit && (
        <PostButton text="Edit Flags" onClick={handleOnEdit} />
      )}
      {!isReadOnly && inEdit && <PostButton text="Save" onClick={handleSave} />}
      {!isReadOnly && <PostButton text="Remove" onClick={handleRemove} />}
    </>
  );
};

function PostButton({
  text,
  onClick,
}: {
  text: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) {
  return (
    <Button
      text={text}
      onClick={onClick}
      className="text-gray-500"
      colorType={ButtonColorTypes.custom}
    />
  );
}
interface AmendmentChildrenProps {
  className: string;
  post: ItemWithMetaData;
}

type PostRatingBehavior = {
  name: string;
  score_type: string;
};

const Body = ({ className, post }: AmendmentChildrenProps) => {
  const [applyLineClamp, setApplyLineClamp] = useState<boolean>(true);
  const [showReadMore, setShowReadMore] = useState<boolean>(true);
  const hasImage = post.item.content.media?.length > 0;
  const containerRef = React.useRef(null);

  useEffect(() => {
    const hasClamping = (el: HTMLParagraphElement) => {
      const { clientHeight, scrollHeight } = el;
      return clientHeight !== scrollHeight;
    };

    const checkButtonAvailability = () => {
      if (!containerRef.current) return;

      setShowReadMore(hasClamping(containerRef.current));
    };

    const debouncedCheck = debounce(checkButtonAvailability, 50);

    checkButtonAvailability();
    window.addEventListener("resize", debouncedCheck);

    return () => {
      window.removeEventListener("resize", debouncedCheck);
    };
  }, [containerRef]);
  const isAffiliation = post.type.name === ItemTypes.AFFILIATION;
  return (
    <div className={className}>
      <div className="flex-1 font-normal text-base leading-6">
        <p
          ref={containerRef}
          className={classNames(
            `text-gray-500 text-xs font-light`,
            applyLineClamp ? "line-clamp-3" : "line-clamp-6"
          )}
        >
          {post.item.content.text}
        </p>
        {showReadMore && (
          <button
            className="text-cyan-500 font-light text-xs"
            onClick={() => setApplyLineClamp(!applyLineClamp)}
          >
            Read {applyLineClamp ? "More" : "Less"}
          </button>
        )}
      </div>
      {hasImage && (
        <div className="flex items-start pr-2 w-32">
          <PostImage
            media={contentMediaToMediaExternalInternal(post.item.content)}
            noPadding={true}
          />
        </div>
      )}
      {isAffiliation &&
        post.item.content.media_urls &&
        post.item.content.media_urls.length > 0 && (
          <div>
            <div className="max-w-[1000px] grid 2xl:grid-cols-2 gap-4 my-4">
              {post.item.content.media_urls.map((imageSrc: string) => (
                <div className="flex items-center" key={imageSrc}>
                  <img
                    src={imageSrc}
                    alt="Pasted"
                    className="max-h-48 max-w-48 rounded-md"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};
const BehaviorFlags = ({ className, post }: AmendmentChildrenProps) => {
  if (justBehaviorRatings(post.rating).length === 0) return <></>;
  return (
    <div className={className}>
      <div className="flex flex-wrap gap-3">
        <RatingBadges
          ratings={justBehaviorRatings(post.rating)}
          showFlagIcon={true}
        />
      </div>
    </div>
  );
};

function ErrorList({ list }: { list: string[] }) {
  return (
    <>
      {list.map((text) => (
        <ErrorMessageContainer message={text} />
      ))}
    </>
  );
}

const Header = ({ className, post }: AmendmentChildrenProps) => {
  const displayDate = post.item.content.created_at
    ? formattedDate(new Date(post.item.content.created_at))
    : "NA";
  const postTypeText = getDisplayPostTypeText(post);
  const isAffiliation = post.type.name === ItemTypes.AFFILIATION;
  return (
    <div className={className}>
      {!isAffiliation && (
        <SocialIcon
          url={post.item.content.profile_url}
          className="pointer-events-none"
          style={{ width: "40px", height: "40px" }}
        />
      )}
      {isAffiliation && (
        <div className="w-10 h-10 p-1 rounded-full bg-sky-800 text-white mr-3 flex items-center justify-center">
          <UserGroupIcon className="w-5 h-5"></UserGroupIcon>
        </div>
      )}
      <div className="flex flex-col ">
        {!isAffiliation && (
          <div className="text-sm font-medium mx-3 text-gray-700">{`${postTypeText} on ${post.source?.name}`}</div>
        )}
        {isAffiliation && (
          <div className="text-sm font-medium mx-3 text-gray-700">{`${post.item.content.affiliation_type}`}</div>
        )}
        <div className="text-sm font-normal mx-3 text-gray-500">
          {displayDate}
        </div>
      </div>
      <div className="flex ml-auto">
        <LinkIcon className="w-5 h-4 text-blue-500" />
        <a
          className="text-gray-500 text-xs ml-1 font-light"
          href={post.item.content.url}
          rel="noreferrer"
          target="_blank"
        >
          View Link
        </a>
      </div>
    </div>
  );
};
